import React, { useState, useEffect, useCallback } from 'react';
import { EmailEditorHeader } from './email-editor/EmailEditorHeader';
import { EmailEditorTabs } from './email-editor/EmailEditorTabs';
import { EmailEditorSkeleton } from './email-editor/EmailEditorSkeleton';
import { ErrorDisplay } from './email-editor/ErrorDisplay';
import { useTemplateEditor } from './email-editor/hooks/useTemplateEditor';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

interface EmailTemplateEditorProps {
  templateId: string;
  onBack: () => void;
  onSave: () => void;
}

export function EmailTemplateEditor({ templateId, onBack, onSave }: EmailTemplateEditorProps) {
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  
  const {
    template,
    editedTemplate,
    loading,
    loadingAction,
    previewHtml,
    variablesJson,
    variablesValid,
    handleInputChange,
    handleSelectChange,
    handleVariablesChange,
    handleSave,
    getPreviewHtmlWithExampleValues,
    error,
    authReady
  } = useTemplateEditor(templateId);
  
  // Effect to handle errors from the hook
  useEffect(() => {
    if (error) {
      console.error('Template editor error:', error);
      setHasError(true);
      setErrorMessage(error.message || 'Failed to load template data');
    } else {
      setHasError(false);
      setErrorMessage('');
    }
  }, [error]);
  
  // Control initial page transition
  useEffect(() => {
    // Set initial load to false after a short delay
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
      console.log("Template editor initial load complete:", {
        hasTemplate: !!template,
        hasEditedTemplate: !!editedTemplate
      });
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [template, editedTemplate]);
  
  // Reset error state if template loads successfully
  useEffect(() => {
    if (template && editedTemplate) {
      console.log("Templates loaded successfully");
      setHasError(false);
      setErrorMessage('');
    }
  }, [template, editedTemplate]);
  
  const handleSaveAndNotify = async () => {
    try {
      const result = await handleSave();
      if (result) {
        toast.success("Template saved successfully");
        onSave();
      } else {
        toast.error("Failed to save template");
      }
    } catch (error: any) {
      console.error("Save error:", error);
      toast.error(`An error occurred while saving: ${error.message}`);
    }
  };
  
  // Retry loading if there was an error
  const handleRetry = useCallback(() => {
    setIsInitialLoad(true);
    setRetryCount(prev => prev + 1);
    setHasError(false);
    setErrorMessage('');
    
    // Force remount by setting key
    setTimeout(() => setIsInitialLoad(false), 500);
  }, []);
  
  // Show authentication error if not authenticated
  if (!authReady && !isInitialLoad) {
    return (
      <div className="w-full p-4 max-w-[1200px] mx-auto">
        <ErrorDisplay message="Authentication required. Please login again." />
        <div className="flex gap-2 mt-4">
          <Button 
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="w-full max-w-[1200px] mx-auto transition-opacity duration-300 ease-in-out p-4" 
      style={{ 
        minHeight: '800px',
        width: '100%',
        opacity: loading && isInitialLoad ? 0.7 : 1,
      }}
    >
      {loading && isInitialLoad ? (
        <div className="w-full">
          <EmailEditorSkeleton onBack={onBack} />
        </div>
      ) : hasError ? (
        <div className="w-full p-4">
          <ErrorDisplay message={errorMessage || "Could not load template data. Please try again."} />
          <div className="flex gap-2 mt-4">
            <Button 
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Go Back
            </Button>
            <Button 
              onClick={handleRetry}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : !template || !editedTemplate ? (
        <div className="w-full p-4">
          <ErrorDisplay message="Could not load template data. Please try again." />
          <div className="flex gap-2 mt-4">
            <Button 
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Go Back
            </Button>
            <Button 
              onClick={handleRetry}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : (
        <div className="w-full">
          <EmailEditorHeader 
            template={template} 
            onBack={onBack} 
            onSave={handleSaveAndNotify}
            loadingAction={loadingAction}
            variablesValid={variablesValid}
          />
          
          <div className="mt-6">
            <EmailEditorTabs
              editedTemplate={editedTemplate}
              previewHtml={previewHtml}
              variablesJson={variablesJson}
              variablesValid={variablesValid}
              onInputChange={handleInputChange}
              onSelectChange={handleSelectChange}
              onVariablesChange={handleVariablesChange}
              getPreviewHtmlWithExampleValues={getPreviewHtmlWithExampleValues}
            />
          </div>
        </div>
      )}
    </div>
  );
}
